import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  styled,
} from '@mui/material'
import { intl } from '../../../../i18n'
import { colorPalette } from '../../../style/colorPalette'
import { Button } from '../../../components/buttons'

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: '600px',
    maxWidth: '600px',
    padding: '32px',
    gap: '16px',
  },
})

const StyledDialogTitle = styled(DialogTitle)({
  fontSize: '15px',
  lineHeight: 1,
  color: colorPalette.monotone[10],
  padding: 0,
})

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

const DescriptionText = styled(Typography)({
  fontSize: '14px',
  color: colorPalette.monotone[5],
  whiteSpace: 'pre-line',
})

const WarningBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'isCancel',
})<{ isCancel?: boolean }>(({ isCancel }) => ({
  backgroundColor: isCancel ? colorPalette.monotone[1] : colorPalette.yellow[0],
  border: `1px solid ${isCancel ? colorPalette.monotone[3] : colorPalette.yellow[5]}`,
  borderRadius: '4px',
  padding: '16px',
  height: '84px',
}))

const WarningText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isCancel',
})<{ isCancel?: boolean }>(({ isCancel }) => ({
  color: isCancel ? colorPalette.monotone[5] : colorPalette.yellow[5],
  whiteSpace: 'pre-line',
  fontSize: '13px',
}))

const StyledDialogActions = styled(DialogActions)({
  padding: 0,
})

const WarningSpan = styled('span')({
  color: colorPalette.pink[6],
})

interface ProjectClosingDialogProps {
  isOpen: boolean
  onSubmit: () => void
  onClose: () => void
  isCancel?: boolean
}

const ProjectClosingDialog: React.FC<ProjectClosingDialogProps> = ({
  isOpen,
  onSubmit,
  onClose,
  isCancel = false,
}) => {
  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledDialogTitle>
        {intl.formatMessage({
          id: isCancel
            ? 'taskActualWorkApprovalStatus.projectClosingCancelDialogTitle'
            : 'taskActualWorkApprovalStatus.projectClosingDialogTitle',
        })}
      </StyledDialogTitle>
      <StyledDialogContent>
        <DescriptionText variant="body1">
          {isCancel ? (
            <>
              {intl.formatMessage({
                id: 'taskActualWorkApprovalStatus.projectClosingCancelDialogDescription',
              })}
              {'\n'}
              <WarningSpan>
                {intl.formatMessage({
                  id: 'taskActualWorkApprovalStatus.projectClosingCancelDialogWarning',
                })}
              </WarningSpan>
            </>
          ) : (
            intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.projectClosingDialogDescription',
            })
          )}
        </DescriptionText>
        <WarningBox isCancel={isCancel}>
          <WarningText isCancel={isCancel} variant="body2">
            {intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.projectClosingDialogOperations',
            })}
          </WarningText>
        </WarningBox>
      </StyledDialogContent>
      <StyledDialogActions>
        <Button
          onClick={onClose}
          colorPattern="monotone"
          variant="outlined"
          size="l"
        >
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        {isCancel ? (
          <Button
            onClick={onSubmit}
            colorPattern="pink"
            variant="filled"
            size="l"
          >
            {intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.projectClosingCancelButton',
            })}
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            colorPattern="skyBlue"
            variant="filled"
            size="l"
          >
            {intl.formatMessage({
              id: 'taskActualWorkApprovalStatus.projectClosing',
            })}
          </Button>
        )}
      </StyledDialogActions>
    </StyledDialog>
  )
}

export default ProjectClosingDialog
